import { reactive } from "vue";

/**
 * Shared state for the donor-funded Pro announcement.
 *
 * The modal opens itself once and then remembers the dismissal, so the header
 * needs both a way to bring it back and a way to know whether there is anything
 * to bring back. That state lives here rather than inside the modal because the
 * header sits in a different subtree and cannot reach it.
 */

/**
 * Dismissal is remembered under a key that carries the season, so next season's
 * announcement gets a fresh key and a fresh chance to be seen.
 */
export const DONOR_PRO_ACCESS_STORAGE_KEY =
  "mypbe_donor_pro_access_2026_2027_dismissed";

/** Qualification closes at midnight Eastern; the server uses the same date. */
export const DONOR_PRO_ACCESS_CUTOFF = new Date(2026, 9, 1);

function readDismissed() {
  try {
    return window.localStorage.getItem(DONOR_PRO_ACCESS_STORAGE_KEY) === "1";
  } catch {
    // Private mode or blocked storage: nothing was remembered, so nothing has
    // been dismissed as far as this page is concerned.
    return false;
  }
}

export const donorProAccess = reactive({
  /**
   * Set by the modal itself. The header has nothing to reopen on a page that
   * doesn't mount one, and an item that opens nothing is worse than no item.
   */
  mounted: false,
  open: false,
  dismissed: readDismissed(),
});

export function isDonorProAccessOfferOpen(now = new Date()) {
  return now < DONOR_PRO_ACCESS_CUTOFF;
}

/**
 * Whether the header should offer a way back to the announcement: only once
 * the visitor has dismissed it, and only while the offer is still open.
 */
export function canReopenDonorProAccess() {
  return (
    donorProAccess.mounted &&
    donorProAccess.dismissed &&
    isDonorProAccessOfferOpen()
  );
}

export function openDonorProAccess() {
  donorProAccess.open = true;
}

/**
 * Closes the modal for good. `dismissed` deliberately stays set when it is
 * reopened from the header and dismissed again — the header item is the way
 * back, so it should not disappear the moment it is used.
 */
export function dismissDonorProAccess() {
  donorProAccess.open = false;
  donorProAccess.dismissed = true;

  try {
    window.localStorage.setItem(DONOR_PRO_ACCESS_STORAGE_KEY, "1");
  } catch {
    /* nothing to do — the modal simply returns on the next visit */
  }
}
